import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { InsuranceType } from '@websky/graphql';
import { canDownloadInsurancePolicy } from '@shared/lib/insurance';
import { EngineTheme, useMergeClasses, useTheme } from '../../../../theme';
import IconButton from '../../../../BaseComponents/Button/components/IconButton/IconButton';
import { InsurancePrice } from '../../InsurancePrice/InsurancePrice';
import { SelectForm } from '../../SelectForm/SelectForm';
import Link from '../../../../Link';
import { OptionsList } from '../../OptionsList/OptionsList';
import { isExistI18nTranslate } from '../../../../utils';
import { CardExtraView } from '@shared/ui/CardExtraView';
import { CdnIcon, Tip } from '@shared/ui';
import CdnImage from '../../../../CdnImage';
import { InsuranceProductViewBaseProps } from '../types';

interface Props extends Omit<InsuranceProductViewBaseProps, 'bannerVariant'> {
	classes?: EngineTheme['InsurancePage']['InsuranceProductView'];
	bannerVariant?: 'icon' | 'title' | 'titleWithIcon';
	fallbackIconUrl?: string;
}

export const InsuranceProductView: FC<Props> = ({
	classes,
	insuranceProgram,
	popular,
	policyUrl,
	onSelect,
	onRemove,
	onDownloadVskInsurance,
	hideForAllPassengersTextBecauseSinglePassenger,
	isShowImportantInsurancesError,
	bannerVariant = 'icon',
	popularPricePosition = 'withinRadio',
	showIconButton,
	isSelected,
	isConfirmed,
	isTouched,
	isError,
	fallbackIconUrl,
	handleToggle
}) => {
	const { t } = useTranslation('InsurancePage');
	const { InsuranceProductView: defaultTheme } = useTheme('InsurancePage');
	const theme = useMergeClasses(defaultTheme, classes);

	const [isLoading, setIsLoading] = useState(false);

	const onDownloadVskInsuranceHandler = useCallback(async () => {
		setIsLoading(true);
		await onDownloadVskInsurance?.();
		setIsLoading(false);
	}, [onDownloadVskInsurance]);

	const showIcon = bannerVariant === 'titleWithIcon' || bannerVariant === 'icon';
	const showTitle = bannerVariant === 'titleWithIcon' || bannerVariant === 'title';

	const fallbackUrl =
		fallbackIconUrl || 'https://cdn.websky.aero/content/frontend/images/ZZ/services/insurance/default.png';

	const infoLink = useMemo(() => {
		const isVskInsurance = insuranceProgram.type === InsuranceType.Vsk;
		const isConfirmedAndCanDownload = canDownloadInsurancePolicy({
			isConfirmed,
			policyUrl,
			insuranceType: insuranceProgram.type
		});

		if (!insuranceProgram.urlToOffer && !isConfirmedAndCanDownload) {
			return null;
		}

		return (
			<div className={theme.linksWrapper}>
				{isExistI18nTranslate('Offer', { ns: 'InsurancePage' }) ? (
					<div className={theme.moreInfo}>
						<CdnIcon icon="Info/Details" className={theme.infoIcon} />

						<span>{t('More details in')}&nbsp;</span>

						<Link className={theme.moreInfo} action={insuranceProgram.urlToOffer} target="_blank">
							{t('Offer')}
						</Link>
					</div>
				) : (
					<Link className={theme.moreInfo} action={insuranceProgram.urlToOffer} target={'_blank'}>
						<CdnIcon icon="Info/Details" className={theme.infoIcon} />

						{t('More details')}
					</Link>
				)}

				{isConfirmedAndCanDownload && (
					<Link
						className={cn(theme.moreInfo, { [theme.moreInfo_loading]: isLoading })}
						action={isVskInsurance ? onDownloadVskInsuranceHandler : policyUrl}
						target={!isVskInsurance ? '_blank' : undefined}
					>
						<CdnIcon icon="Action/Download" className={theme.infoIcon} />

						{t('Download police')}
					</Link>
				)}
			</div>
		);
	}, [
		insuranceProgram.urlToOffer,
		insuranceProgram.type,
		policyUrl,
		isConfirmed,
		onDownloadVskInsuranceHandler,
		isLoading
	]);

	const content = (
		<>
			{showIconButton && (
				<IconButton
					onClick={handleToggle}
					isSuccess={isSelected || isConfirmed}
					className={cn(theme.selectButton, {
						[theme.confirmed]: isConfirmed
					})}
				>
					<CdnIcon icon="Action/Add" />
				</IconButton>
			)}

			<div className={theme.contentWrapper}>
				{bannerVariant === 'icon' && <div className={theme.title}>{insuranceProgram.name}</div>}
				{insuranceProgram.description && (
					<div
						className={theme.description}
						dangerouslySetInnerHTML={{ __html: insuranceProgram.description }}
					/>
				)}
				{!!insuranceProgram.options?.length && (
					<OptionsList className={theme.options} options={insuranceProgram.options.map(({ name }) => name)} />
				)}
			</div>
		</>
	);

	const footer = (
		<>
			{infoLink}
			{popular && !isConfirmed && (
				<SelectForm
					classes={{ label: theme.label, label__agreement: theme.label__agreement }}
					onSelect={() => onSelect(insuranceProgram.code)}
					onRemove={() => onRemove(insuranceProgram.code)}
					selected={isSelected}
					hideForAllPassengersTextBecauseSinglePassenger={hideForAllPassengersTextBecauseSinglePassenger}
					isTouched={isTouched}
					isError={isError && isShowImportantInsurancesError}
					price={popularPricePosition === 'withinRadio' ? insuranceProgram.totalPrice : undefined}
				/>
			)}

			{popular && popularPricePosition === 'bottom' && (
				<InsurancePrice
					selected={isSelected}
					price={insuranceProgram.totalPrice}
					variant="bottomPrice"
					hideForAllPassengersTextBecauseSinglePassenger={false}
				/>
			)}

			{!popular && (
				<InsurancePrice
					className={theme.footerPrice}
					selected={isSelected}
					price={insuranceProgram.totalPrice}
					variant="regular"
					onClick={!isConfirmed ? handleToggle : undefined}
					onRemove={handleToggle}
					hideForAllPassengersTextBecauseSinglePassenger={hideForAllPassengersTextBecauseSinglePassenger}
				/>
			)}
		</>
	);

	return (
		<CardExtraView
			classes={{
				root: cn(theme.root, {
					[theme.popular]: popular,
					[theme.confirmed]: isConfirmed
				}),
				root_insurance: theme.root_insurance,
				header: theme.header,
				header__icon: theme.header__icon,
				header__badge: theme.header__badge,
				header__title: theme.header__title,
				content: theme.content,
				footer: theme.footer
			}}
			type="insurance"
			content={content}
			footerControls={footer}
			icon={
				showIcon ? (
					<CdnImage path={`services/insurance/${insuranceProgram.code}.png`} fallbackUrl={fallbackUrl} />
				) : undefined
			}
			badge={popular && <Tip variant="popular" title={t('Most popular')} />}
			title={showTitle ? insuranceProgram.name : undefined}
		/>
	);
};
